import React, { useRef, useEffect, useCallback, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Grid,
  IconButton,
  TextField,
  alpha
} from '@mui/material';
import {
  FolderOpen,
  CloudUpload,
  Delete,
  PictureAsPdf,
  Image as ImageIcon,
  Movie,
  Code,
  Save
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../api/axiosConfig';
import { isAcceptedFile, isVideoLike, maxSizeForType } from '../../utils/documentTypes';

const MAX_FILES = 5;

const CreateDocuments = ({
  playerId,
  markDirty,
  isSubmitting,
  setIsSubmitting,
  onSuccess
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]); // [{file: File, titre: '', previewUrl: null}]
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      files.forEach(f => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
    };
  }, []);

  const addFiles = useCallback((newFiles) => {
    const remaining = MAX_FILES - files.length;
    if (remaining <= 0) {
      alert(t('playerCardCreator.documentsForm.maxFiles', { max: MAX_FILES }));
      return;
    }

    const toAdd = [];
    for (let i = 0; i < Math.min(newFiles.length, remaining); i++) {
      const file = newFiles[i];
      if (!isAcceptedFile(file)) {
        alert(t('playerCardCreator.documentsForm.invalidFileType'));
        continue;
      }
      const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
      if (file.size > maxSizeForType(file.type || ext)) {
        alert(t('playerCardCreator.documentsForm.fileTooLarge'));
        continue;
      }
      const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
      toAdd.push({ file, titre: '', previewUrl });
    }

    if (toAdd.length > 0) {
      setFiles(prev => [...prev, ...toAdd]);
      markDirty();
    }
  }, [files.length, markDirty, t]);

  // Paste handler
  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const pastedFiles = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          e.preventDefault();
          const file = items[i].getAsFile();
          if (file) {
            const extension = file.type.split('/')[1] || 'png';
            const newFile = new File([file], `screenshot_${Date.now()}.${extension}`, { type: file.type });
            pastedFiles.push(newFile);
          }
        }
      }
      if (pastedFiles.length > 0) addFiles(pastedFiles);
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [addFiles]);

  const handleFileSelect = useCallback((e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0) addFiles(selectedFiles);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [addFiles]);

  const handleDragEnter = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }, []);
  const handleDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files || []);
    if (droppedFiles.length > 0) addFiles(droppedFiles);
  }, [addFiles]);

  const handleRemoveFile = useCallback((index) => {
    setFiles(prev => {
      const removed = prev[index];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleTitreChange = useCallback((index, value) => {
    setFiles(prev => prev.map((f, i) => i === index ? { ...f, titre: value } : f));
    markDirty();
  }, [markDirty]);

  const handleUpload = async () => {
    if (!playerId || files.length === 0 || uploading) return;

    setUploading(true);
    if (setIsSubmitting) setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('player_id', playerId);
      files.forEach((f, i) => {
        formData.append('documents', f.file);
        formData.append(`titres[${i}]`, f.titre || '');
      });

      await api.post('/api/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      alert(t('playerCardCreator.documentsForm.uploadSuccess'));

      // Cleanup
      files.forEach(f => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
      setFiles([]);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error uploading documents:', error);
      alert(t('playerCardCreator.documentsForm.uploadError'));
    } finally {
      setUploading(false);
      if (setIsSubmitting) setIsSubmitting(false);
    }
  };

  const compactSectionStyle = {
    background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
    backdropFilter: 'blur(8px)',
    borderRadius: 2,
    border: '1px solid rgba(255,255,255,0.3)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    p: { xs: 1.5, md: 2 },
    mb: { xs: 1.5, md: 2 }
  };

  const iconStyle = {
    mr: 1.5,
    color: 'primary.main',
    fontSize: '1.3rem'
  };

  return (
    <Box sx={{ minHeight: { xs: 'auto', md: '40vh' }, p: 0, pb: { xs: 4, md: 0 } }}>
      <Box sx={{ width: '100%', margin: 0, padding: 0, overflowX: 'hidden' }}>

        {/* Upload Zone */}
        <Paper elevation={0} sx={compactSectionStyle}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FolderOpen sx={iconStyle} />
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
              {t('playerCardCreator.documentsForm.title')}
            </Typography>
            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              ({files.length}/{MAX_FILES})
            </Typography>
          </Box>

          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,image/heic,image/heif,.heic,.heif,video/quicktime,.mov,video/mp4,.mp4,text/html,.html,.htm"
            multiple
            style={{ display: 'none' }}
          />

          {/* Drop zone - only show if not at max */}
          {files.length < MAX_FILES && (
            <Box
              onClick={() => fileInputRef.current?.click()}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              sx={{
                border: '2px dashed',
                borderColor: isDragging ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragging
                  ? (theme) => alpha(theme.palette.primary.main, 0.05)
                  : 'grey.50',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.02)
                },
                mb: files.length > 0 ? 2 : 0
              }}
            >
              <CloudUpload sx={{ fontSize: 40, color: isDragging ? 'primary.main' : 'grey.400', mb: 1 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {t('playerCardCreator.documentsForm.dropOrClick')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('playerCardCreator.documentsForm.pasteHint')}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  JPG, PNG, GIF, WebP, HEIC, PDF, MOV, MP4, HTML • Max 10MB / 20MB (.mov/.mp4) • {t('playerCardCreator.documentsForm.maxFilesLabel', { max: MAX_FILES })}
                </Typography>
              </Box>
            </Box>
          )}

          {/* File list */}
          {files.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {files.map((fileItem, index) => (
                <Box
                  key={index}
                  sx={{
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 2,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    backgroundColor: 'grey.50'
                  }}
                >
                  {/* Preview */}
                  {fileItem.previewUrl ? (
                    <Box
                      component="img"
                      src={fileItem.previewUrl}
                      alt="Preview"
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        flexShrink: 0
                      }}
                    />
                  ) : fileItem.file.type === 'application/pdf' ? (
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f44336',
                        borderRadius: 1,
                        flexShrink: 0
                      }}
                    >
                      <PictureAsPdf sx={{ fontSize: 30, color: 'white' }} />
                    </Box>
                  ) : (isVideoLike(fileItem.file.type) || /\.(mov|mp4)$/i.test(fileItem.file.name)) ? (
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#1e293b',
                        borderRadius: 1,
                        flexShrink: 0
                      }}
                    >
                      <Movie sx={{ fontSize: 30, color: 'white' }} />
                    </Box>
                  ) : (fileItem.file.type === 'text/html' || /\.html?$/i.test(fileItem.file.name)) ? (
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#e67e22',
                        borderRadius: 1,
                        flexShrink: 0
                      }}
                    >
                      <Code sx={{ fontSize: 30, color: 'white' }} />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'grey.200',
                        borderRadius: 1,
                        flexShrink: 0
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 30, color: 'grey.500' }} />
                    </Box>
                  )}

                  {/* File info + titre */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={500} noWrap sx={{ mb: 0.5 }}>
                      {fileItem.file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {(fileItem.file.size / 1024).toFixed(1)} KB
                    </Typography>
                    <TextField
                      size="small"
                      placeholder={t('playerCardCreator.documentsForm.addTitle')}
                      value={fileItem.titre}
                      onChange={(e) => handleTitreChange(index, e.target.value)}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          fontSize: '0.85rem'
                        },
                        '& .MuiOutlinedInput-input': {
                          py: 0.5,
                          px: 1
                        }
                      }}
                    />
                  </Box>

                  {/* Remove button */}
                  <IconButton
                    onClick={() => handleRemoveFile(index)}
                    size="small"
                    sx={{
                      color: 'error.main',
                      flexShrink: 0,
                      '&:hover': {
                        backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1)
                      }
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Paper>

        {/* Save Button */}
        {files.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: { xs: 2, md: 0 } }}>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={uploading || !playerId}
              startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <Save />}
              sx={{
                background: uploading
                  ? 'rgba(0,0,0,0.12)'
                  : 'linear-gradient(45deg, #A29BFE, #6C5CE7)',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 6px 20px rgba(162, 155, 254, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: uploading
                    ? 'rgba(0,0,0,0.12)'
                    : 'linear-gradient(45deg, #6C5CE7, #5A4BD1)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 8px 25px rgba(162, 155, 254, 0.4)'
                },
                '&:disabled': {
                  background: 'rgba(0,0,0,0.12)',
                  color: 'rgba(0,0,0,0.26)'
                }
              }}
            >
              {uploading
                ? t('playerCardCreator.documentsForm.uploading')
                : t('playerCardCreator.documentsForm.uploadButton', { count: files.length })
              }
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CreateDocuments;
